import { Document } from '@components/chat/types'
import { getColorByFiletype, getIcon } from '@components/chat/utils'
import { listItemTextClasses, styled, tooltipClasses, TooltipProps } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const LIMIT = 3

type Props = {
  documents: Document[]
}

export const DocumentList = ({ documents, order }: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    setIsCollapsed(false)
  }, [documents])
  if (documents.length === 0) return null

  return (
    <Box
      order={order}
      mt={-3}
    >
      <List dense>
        <Collapse in>
          {documents.slice(0, LIMIT).map((d) => (
            <DocumentRow
              document={d}
              key={d.filename}
            />
          ))}
        </Collapse>
        <Collapse in={isCollapsed}>
          {documents.slice(LIMIT).map((d) => (
            <DocumentRow
              document={d}
              key={d.filename}
            />
          ))}
        </Collapse>
      </List>
      {documents.length > LIMIT && (
        <Box
          width="100%"
          display="flex"
          justifyContent="center"
        >
          <Button
            variant="text"
            color="primary"
            onClick={() => setIsCollapsed((prev) => !prev)}
            sx={{ mt: -2, textTransform: 'none', color: 'primary.dark' }}
          >
            {t(isCollapsed ? 'index.documents.show_less' : 'index.documents.show_more')}
          </Button>
        </Box>
      )}
    </Box>
  )
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[12],
    fontSize: 11,
    maxWidth: 'none',
  },
}))

const DocumentRow = ({ document }: { document: Document }) => (
  <ListItemButton
    target="_blank"
    href={document.file_uri}
    // onClick={(e) => {
    //   e.stopPropagation()
    //   e.preventDefault()
    //   fetch(document.file_uri, {})
    //     .then((response) => response.blob())
    //     .then((blob) => {
    //       const downloadUrl = URL.createObjectURL(blob)
    //       const a = document.createElement('a')
    //       a.href = downloadUrl
    //       a.download = document.filename // Set desired file name here
    //       document.body.appendChild(a)
    //       a.click()
    //       a.remove()
    //       URL.revokeObjectURL(downloadUrl)
    //     })
    // }}
  >
    <ListItemIcon
      sx={{ flexShrink: 1, maxWidth: '36px', minWidth: '36px', color: getColorByFiletype(document.metadata?.filetype) }}
    >
      {getIcon(document.metadata?.filetype)}
    </ListItemIcon>
    <LightTooltip
      title={document?.relative_path || ''}
      placement="top"
      sx={{ flex: 1, flexGrow: 1 }}
    >
      <ListItemText
        sx={{
          display: 'flex',
          gap: 1,
          margin: 0,
          [`& .${listItemTextClasses.primary}`]: {
            underline: 'none',
            fontWeight: 'bold',
            cursor: 'pointer',
            color: 'primary.main',
            transition: 'color 0.3s',
          },
          '&:hover': {
            [`& .${listItemTextClasses.primary}`]: {
              color: 'primary.dark',
            },
          },
        }}
        primary={document.filename}
        secondary={document.metadata?.page_numbers ? `Page ${document.metadata.page_numbers.toString()}` : undefined}
      />
    </LightTooltip>
  </ListItemButton>
)
